import React, { FC, Fragment } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer, { TableContainerProps } from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { FormikErrors, FormikTouched } from 'formik';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { EditIcon, TrashIcon } from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT, DAY_FORMAT } from 'src/shared/constants';
import { AvailabilityStatusEnums, CycleTypeEnums, OperationTypeEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { formatTime, getTimeFromSeconds } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

import {
  AvailabilityCycleInputsType,
  EditedPeriodDateType,
  IAvailabilityForm,
  PreferenceInputsType,
} from './availabilities.types';
import { getStatusColor, mapAvailabilityStatusEnum } from './availabilities.utils';
import { EmployeeAvailabilityEditTable } from './EmployeeAvailabilityEditTable';

interface Props {
  inputs: AvailabilityCycleInputsType | undefined;
  editedPeriodDate: EditedPeriodDateType | undefined;
  loading: boolean;
  updating: boolean;
  onEditingChange: (periodKey: string, dateKey: string) => void;
  onCancel: () => void;
  onChangeFullDay: (wasAllday: boolean, periodKey: string, dateKey: string) => void;
  onChangeStatus: (
    status: AvailabilityStatusEnums,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => void;
  onDelete: (periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onAdd: (periodKey: string, dateKey: string) => void;
  onChangeStartTime: (value: string | undefined, periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onChangeEndTime: (value: string | undefined, periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onSave: () => void;
  onDeleteDate: (periodKey: string, dateKey: string) => void;
  touched?: FormikTouched<AvailabilityCycleInputsType>;
  errors?: FormikErrors<AvailabilityCycleInputsType>;
  readOnly?: boolean;
}

export const EmployeeAvailabilityCyclePreferencesTable: FC<Props> = ({
  inputs,
  editedPeriodDate,
  loading,
  updating,
  onEditingChange,
  onCancel,
  onChangeFullDay,
  onChangeStatus,
  onDelete,
  onAdd,
  onChangeStartTime,
  onChangeEndTime,
  onSave,
  onDeleteDate,
  touched,
  errors,
  readOnly,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const timeFormat = useSelector(getTimeFormat);

  if (loading || !inputs) {
    return <Skeleton className="rounded" variant="rectangular" animation="wave" height="280px" />;
  }

  return Object.entries(inputs.groups).map(([periodKey, periodGroups], periodIndex) => (
    <Stack key={periodKey} direction="column" gap={2} paddingLeft={1}>
      {inputs.type === CycleTypeEnums.BI_WEEKLY && (
        <Typography fontSize="1.25rem" fontWeight={400} color="text.secondary">
          {t('availability.week')} {periodIndex + 1}
        </Typography>
      )}
      <TableContainer
        sx={{
          ...defaultTableStyle,
          '#edit-cell': { visibility: 'hidden' },
          ...(isNil(editedPeriodDate) && !readOnly
            ? { 'tr:hover td, td:hover': { '& #edit-cell': { visibility: 'visible' } } }
            : {}),
        }}
      >
        <Table padding="none" sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            {
              // Group availabilities per date in order to display time ranges on the shared date
              Object.entries(periodGroups).map(([dateKey, dateGroups], dateIndex) => {
                const filteredDateGroups = dateGroups.filter(
                  (dateGroup) => dateGroup.operationType !== OperationTypeEnums.DELETE,
                );

                const [sharedPreference] = filteredDateGroups;
                const isNotSpecified = !filteredDateGroups.some(({ id }) => !isNil(id));
                const isEditMode = editedPeriodDate?.periodKey === periodKey && editedPeriodDate.dateKey === dateKey;

                const isFullDay = !filteredDateGroups.some(
                  ({ startTimeInSeconds, endTimeInSeconds }) => !isNil(startTimeInSeconds) || !isNil(endTimeInSeconds),
                );

                const isAllUnavailable = filteredDateGroups.every(
                  ({ status }) => status === AvailabilityStatusEnums.NOT_AVAILABLE,
                );

                return (
                  <Fragment key={`${periodKey}-${dateKey}-${dateIndex}`}>
                    <TableRow>
                      <TableCell sx={{ paddingBottom: isEditMode ? '0 !important' : undefined }}>
                        {inputs.type === CycleTypeEnums.DAYS ? (
                          <Stack direction="column" gap={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={!isEditMode ? 'text.primary' : 'primary.main'}
                            >
                              {t('availability.day')} {sharedPreference.dayOfCycle}
                            </Typography>
                            <Typography
                              fontSize="0.75rem"
                              fontWeight={400}
                              className="text-muted"
                              color={!isEditMode ? 'text.primary' : 'primary.main'}
                            >
                              {moment(dateKey).format(DAY_DATE_MONTH_FORMAT)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={!isEditMode ? 'text.primary' : 'primary.main'}
                          >
                            {moment(dateKey).format(DAY_FORMAT)}
                          </Typography>
                        )}
                      </TableCell>
                      {!isEditMode && (
                        <>
                          <TableCell>
                            {isNotSpecified ? (
                              <Typography variant="body2" color="text.secondary">
                                {t('availability.notSpecified')}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: getStatusColor(
                                    theme,
                                    isAllUnavailable
                                      ? AvailabilityStatusEnums.NOT_AVAILABLE
                                      : AvailabilityStatusEnums.AVAILABLE,
                                  ),
                                }}
                              >
                                {isFullDay ? t('availability.allDay') : t('availability.timeRange')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="column">
                              {!isNil(sharedPreference.id) &&
                                filteredDateGroups.map((availability, availabilityIndex) => {
                                  const textColor = getStatusColor(theme, availability.status);
                                  const key = `grouped-availabilities-status-${availabilityIndex}`;

                                  return (
                                    <Typography key={key} variant="body2" sx={{ color: textColor }}>
                                      {mapAvailabilityStatusEnum(availability.status, t)}
                                    </Typography>
                                  );
                                })}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {!isNotSpecified &&
                              !isFullDay &&
                              filteredDateGroups.map((availability, availabilityIndex) => {
                                const textColor = getStatusColor(theme, availability.status);
                                const key = `grouped-availabilities-times-${availabilityIndex}`;

                                return (
                                  <Stack key={key} direction="column">
                                    <Stack direction="row" gap={0.5}>
                                      <Typography variant="body2" sx={{ color: textColor }}>
                                        {formatTime(getTimeFromSeconds(availability.startTimeInSeconds), timeFormat)}
                                      </Typography>
                                      {' - '}
                                      <Typography variant="body2" sx={{ color: textColor }}>
                                        {formatTime(getTimeFromSeconds(availability.endTimeInSeconds), timeFormat)}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                );
                              })}
                          </TableCell>
                          <ComponentWithPermissions permissions={PermissionsEnums.WORK_TIME_PREFERENCES_EDIT}>
                            {!inputs.isLocked && (
                              <TableCell width="15%">
                                <Stack direction="row" alignItems="center" id="edit-cell">
                                  <IconButton onClick={() => onEditingChange(periodKey, dateKey)}>
                                    <EditIcon size={20} />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    onClick={() => onDeleteDate(periodKey, dateKey)}
                                    disabled={isNotSpecified}
                                  >
                                    <TrashIcon size={20} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            )}
                          </ComponentWithPermissions>
                        </>
                      )}
                    </TableRow>

                    {isEditMode && (
                      <TableRow sx={{ p: 0 }}>
                        <TableCell colSpan={5} sx={{ p: '1rem 1rem 1rem 0 !important' }}>
                          <Stack
                            padding="1rem"
                            direction="column"
                            sx={{
                              background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#fbfbfb',
                            }}
                          >
                            <EmployeeAvailabilityEditTable
                              isEditMode={isEditMode}
                              isFullDay={isFullDay}
                              availabilities={mapPreferenceInputsToFormData(dateGroups)}
                              getErrors={(preferenceIndex, key) => {
                                const error = errors?.groups?.[periodKey]?.[dateKey]?.[preferenceIndex];
                                const preferenceError = !!error && typeof error !== 'string' ? error : undefined;
                                return preferenceError?.[key];
                              }}
                              getTouched={(preferenceIndex) =>
                                typeof touched?.groups === 'object'
                                  ? !!touched.groups?.[periodKey]?.[dateKey]?.[preferenceIndex]
                                  : false
                              }
                              onChangeFullDay={(wasAllDay) => onChangeFullDay(wasAllDay, periodKey, dateKey)}
                              onChangeStatus={(status, preferenceIndex) =>
                                onChangeStatus(status, periodKey, dateKey, preferenceIndex)
                              }
                              onDelete={(preferenceIndex) => onDelete(periodKey, dateKey, preferenceIndex)}
                              onAdd={() => onAdd(periodKey, dateKey)}
                              onChangeStartTime={(value, preferenceIndex) =>
                                onChangeStartTime(value, periodKey, dateKey, preferenceIndex)
                              }
                              onChangeEndTime={(value, preferenceIndex) =>
                                onChangeEndTime(value, periodKey, dateKey, preferenceIndex)
                              }
                              disabled={updating}
                            />

                            <Stack direction="row" alignItems="center" gap={2} marginTop={2}>
                              <Button color="secondary" onClick={onCancel}>
                                {t('global.cancel')}
                              </Button>
                              <LoadingButton variant="contained" onClick={onSave} loading={updating}>
                                {t('global.save')}
                              </LoadingButton>
                            </Stack>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  ));
};

const defaultTableStyle: TableContainerProps['sx'] = {
  border: 'none',
  td: { verticalAlign: 'middle' },
  'td, th': { border: 'none', p: 1 },
};

const mapPreferenceInputsToFormData = (preferenceInputs: PreferenceInputsType[]): IAvailabilityForm[] => {
  return preferenceInputs.map((preference) => ({
    id: preference.id,
    status: preference.status,
    startTimeInSeconds: preference.startTimeInSeconds,
    endTimeInSeconds: preference.endTimeInSeconds,
    operationType: preference.operationType,
  }));
};
